import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../contexts/AuthContext'
import {
  supportApi,
  supportKeys,
  supportQueryRetry,
  supportSideForPersona,
  type SupportOptions,
  type SupportSide,
} from '../../lib/support'

export interface SupportEntry {
  /** The side the active persona asks as; null for the platform (Admin) and for nobody. */
  side: SupportSide | null
  /** True once GET /api/support/options answered for that side. */
  available: boolean
  /** Still asking the server. */
  loading: boolean
  options: SupportOptions | null
  /** False for a shop member who can't read orders: no order buttons then. */
  canLinkOrders: boolean
}

/**
 * Whether Help & support is there for the active persona. It is only once
 * GET /api/support/options answers: a 404 (a service-core without V170), a 403
 * (not that side after all) or any other failure leaves every entry hidden, so
 * the backoffice looks exactly as it did before.
 */
export function useSupportEntry(): SupportEntry {
  const { activePersona } = useAuth()
  const side = supportSideForPersona(activePersona)
  const query = useQuery({
    queryKey: supportKeys.options(side ?? 'shop'),
    queryFn: () => supportApi.options(side as SupportSide),
    enabled: side !== null,
    staleTime: 10 * 60_000,
    // 400, 403 and 404 are answers, not hiccups: asking again changes nothing.
    retry: supportQueryRetry,
  })
  const available = side !== null && query.isSuccess
  const options = available ? query.data ?? null : null
  return {
    side,
    available,
    loading: side !== null && query.isPending,
    options,
    canLinkOrders: available && options?.canLinkOrders !== false,
  }
}
